import type { FC } from 'react';
import * as Sentry from '@sentry/browser';
import type { RequestParams } from 'loti-request';
import { useTranslation } from 'react-i18next';

import notifier from 'app/components/Notification';

import { isPublicError, isPrivateError, isParamsError, isUnknownError } from './utils';
import type { ServerError } from './types';

const translateMap: { [key: string]: string | undefined } = {
  videogame_not_found: 'notif-server_error-videogame_not_found',
  serie_not_found: 'notif-server_error-serie_not_found',
  match_not_found: 'notif-server_error-match_not_found',
  match_bad_blueprint_videogame: 'notif-server_error-match_bad_blueprint_videogame',
  match_no_blueprint_assigned: 'notif-server_error-match_no_blueprint_assigned',
  match_not_started: 'notif-server_error-match_not_started',
  match_postponed: 'notif-server_error-match_postponed',
  match_running: 'notif-server_error-match_running',
  match_finished: 'notif-server_error-match_finished',
  match_canceled: 'notif-server_error-match_canceled',
  match_booked: 'notif-server_error-match_booked',
  match_not_booked: 'notif-server_error-match_not_booked',
  match_not_booked_by_pandascore: 'notif-server_error-match_not_booked_by_pandascore',
  match_not_two_opponents: 'notif-server_error-match_not_two_opponents',
  match_reviewed: 'notif-server_error-match_reviewed',
  match_not_reviewed_by_pandascore: 'notif-server_error-match_not_reviewed_by_pandascore',
  match_action_not_allowed: 'notif-server_error-match_action_not_allowed',
  match_markets_created: 'notif-server_error-match_markets_created',
  match_markets_not_created: 'notif-server_error-match_markets_not_created',
  match_not_all_markets_settled: 'notif-server_error-match_not_all_markets_settled',
  match_settled: 'notif-server_error-match_settled',
  match_not_settled: 'notif-server_error-match_not_settled',
  match_already_has_this_coverage: 'notif-server_error-match_already_has_this_coverage',
  match_coverage_is_not_valid: 'notif-server_error-match_coverage_is_not_valid',
  match_hasnt_live_odds_coverage: 'notif-server_error-match_hasnt_live_odds_coverage',
  match_no_trader_assigned: 'notif-server_error-match_no_trader_assigned',
  match_has_different_games_than_stats: 'notif-server_error-match_has_different_games_than_stats',
  match_start_not_allowed_because_trader_inputs_are_locked:
    'notif-server_error-match_start_not_allowed_because_trader_inputs_are_locked',
  game_not_found: 'notif-server_error-game_not_found',
  game_not_started: 'notif-server_error-game_not_started',
  game_running: 'notif-server_error-game_running',
  game_finished: 'notif-server_error-game_finished',
  game_not_played: 'notif-server_error-game_not_played',
  game_action_not_allowed: 'notif-server_error-game_action_not_allowed',
  game_invalid_final_round_score: 'notif-server_error-game_invalid_final_round_score',
  market_not_found: 'notif-server_error-market_not_found',
  market_already_reviewed: 'notif-server_error-market_already_reviewed',
  market_status_transition_not_allowed: 'notif-server_error-market_status_transition_not_allowed',
  market_unknown_status: 'notif-server_error-market_unknown_status',
  market_unknown_result: 'notif-server_error-market_unknown_result',
  market_unknown_outcome_result: 'notif-server_error-market_unknown_outcome_result',
  market_bad_results_count: 'notif-server_error-market_bad_results_count',
  market_need_one_outcome_winner_or_all_void:
    'notif-server_error-market_need_one_outcome_winner_or_all_void',
  market_all_lose_settlement: 'notif-server_error-market_all_lose_settlement',
  market_outcomes_without_probability_or_non_void:
    'notif-server_error-market_outcomes_without_probability_or_non_void',
  blueprint_not_found: 'notif-server_error-blueprint-not_found',
  blueprint_fail_insert: 'notif-server_error-blueprint_fail_insert',
  blueprint_fail_delete: 'notif-server_error-blueprint_fail_delete',
  blueprint_one_per_videogame: 'notif-server_error-blueprint_one_per_videogame',
  tier_not_found: 'notif-server_error-tier_not_found',
  tier_auto_booking_already_disabled: 'notif-server_error-tier_auto_booking_already_disabled',
  tier_auto_booking_already_enabled: 'notif-server_error-tier_auto_booking_already_enabled',
  user_not_found: 'notif-server_error-user_not_found',
  user_not_trader: 'notif-server_error-user_not_trader',
  incident_fail_insert: 'notif-server_error-incident_fail_insert',
  incident_fail_update: 'notif-server_error-incident_fail_update',
  incident_fail_delete: 'notif-server_error-incident_fail_delete',
  incident_not_found: 'notif-server_error-incident_not_found',
  incident_empty_notes: 'notif-server_error-incident_empty_notes',
  incident_empty_private_notes: 'notif-server_error-incident_empty_private_notes',
  incident_not_empty_notes: 'notif-server_error-incident_not_empty_notes',
  trader_inputs_inputs_locked: 'notif-server_error-trader_inputs_inputs_locked',
  settlements_settlement_updated_since: 'notif-server_error-settlements_settlement_updated_since'
};

const ServerErrorView: FC<{ error: ServerError | null }> = ({ error }) => {
  const [t] = useTranslation();

  if (error && isPublicError(error)) {
    const key = `${error.type}_${error.error}`;
    const translateKey = translateMap[key] || 'notif-server_error';

    return <div>{t(translateKey)}</div>;
  }

  if (error && isPrivateError(error)) {
    return (
      <div>
        <div>{error.error}</div>
        {Object.keys(error.tags).map(key => (
          <div key={key}>
            {key}: {error.tags[key]}
          </div>
        ))}
      </div>
    );
  }

  return <div>{t('notif-server_error')}</div>;
};

function createServerErrorNotification(response: ServerError | null, params: RequestParams) {
  notifier({
    type: 'warning',
    message: <ServerErrorView error={response} />
  });

  if (response === null) {
    Sentry.withScope(scope => {
      const message = `HTTP null Error : ${response}`;
      scope.setExtra('params', params);
      scope.setExtra('response', response);
      Sentry.captureException(message);
    });
  } else if (isParamsError(response)) {
    Sentry.withScope(scope => {
      const message = `HTTP Error : ${response.error}`;
      scope.setExtra('tags', response.tags);
      scope.setExtra('params', params);
      scope.setExtra('response', response);
      Sentry.captureException(message);
    });
  } else if (isUnknownError(response)) {
    Sentry.withScope(scope => {
      const message = `HTTP Unknown Error : ${response.error}`;
      scope.setExtra('params', params);
      scope.setExtra('response', response);
      Sentry.captureException(message);
    });
  }
}

export default createServerErrorNotification;
